import { formatForDisplay } from "@tanstack/react-hotkeys";
import classNames from "classnames";

import styles from "./Kbd.module.css";
import type { Hotkey } from "./useHotkey";

export interface KbdProps {
  /**
   * The shortcut to render, in the same platform-neutral form passed to
   * `useHotkey` — so the binding and its label cannot drift apart.
   */
  hotkey: Hotkey;
  /** Extra class(es) for call-site layout. */
  className?: string;
}

/**
 * Renders a keyboard shortcut, resolved for the viewer's platform.
 *
 * `"Mod+K"` renders as `⌘K` on Apple platforms and `Ctrl+K` elsewhere;
 * `"Escape"` renders as `Esc` everywhere. Taking the same `Hotkey` value the
 * binding uses is the point — a hardcoded label can go stale when the binding
 * changes, a derived one cannot.
 *
 * Rendered `aria-hidden`: the glyphs read poorly aloud (`⌘` is announced
 * inconsistently across screen readers), so the accessible name belongs on
 * the control this decorates — a button's `aria-label` or its tooltip.
 *
 * @example
 * ```tsx
 * <RobotoButton aria-label="Quick search">
 *   <Kbd hotkey="Mod+K" />
 * </RobotoButton>
 * ```
 */
export function Kbd({ hotkey, className }: KbdProps) {
  return (
    <kbd className={classNames(styles.kbd, className)} aria-hidden="true">
      {formatForDisplay(hotkey)}
    </kbd>
  );
}
